import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { withFormik, Field, Form, getIn } from 'formik';
import camelCaseRecursive from 'camelcase-keys-recursive';
import * as Yup from 'yup';
import { Button, Row, Col, Table } from 'react-bootstrap';

import { indexEmployeesRequest } from '../../requests/employees';
import {
  ButtonTooltip,
  DatatableEmployeeName,
  FormikDatePicker,
  FormikNumber,
  FormikSelect,
  Icon,
  SimpleCenteredModal,
  EmployeeSearchModal
} from '../../components';
import { selectorTypes, hourValues } from './FormOptions';

const basicOvertime = () => ({
  date: '',
  employeeId: '',
  hourValue: 'fifty',
  hours: '',
  minutes: '',
  overtimeType: ''
});

const OvertimesForm = props => {
  const { setFieldValue, setFieldTouched, values, isSubmitting } = props;
  const [selector, setSelector] = useState(selectorTypes);
  const [modalShow, setModalShow] = useState(false);
  const dispatch = useDispatch();

  const handleSuccessIndex = response => {
    const vEmployees = camelCaseRecursive(response.data.data);
    const vSelector = [...selectorTypes, ...vEmployees];
    setSelector(vSelector);
  };

  const fetchEmployees = () => {
    indexEmployeesRequest({
      dispatch,
      params: { active: true, is_dt: false, paginate: false },
      successCallback: handleSuccessIndex
    });
  };

  useEffect(fetchEmployees, []);

  const addAllEmployees = () => {
    const vSelector = selector.filter(selected => selected.id !== undefined);
    const employeesToAdd = vSelector.map(employee => ({ ...basicOvertime(), employeeId: employee.value, employee }));
    const { overtimes } = values;
    const vOvertimes = [...overtimes, ...employeesToAdd];
    setSelector([]);
    setFieldValue('overtimes', vOvertimes);
  };

  const handleSelector = () => {
    const { employeeSelector } = props.values;
    const vSelector = selector.filter(selected => selected.value !== employeeSelector);
    if (vSelector.length === selector.length) {
      return;
    }
    if (employeeSelector === 'all_employees') {
      addAllEmployees();
    } else {
      const employeeToAdd = selector.find(selected => selected.value === employeeSelector);
      const { overtimes } = values;
      const overtimesLength = overtimes.length;
      setFieldValue(`overtimes[${overtimesLength}]`, {
        ...basicOvertime(),
        employeeId: employeeToAdd.value,
        employee: employeeToAdd
      });
      setSelector(vSelector);
    }
  };

  const removeFromTable = (employee, action) => {
    if (action === 'destroy') {
      const { overtimes } = values;
      const notRemovedEmployees = overtimes.filter(selected => selected.employee.id !== employee.id);
      const addToSelector = [...selector, employee];
      setSelector(addToSelector);
      setFieldValue('overtimes', notRemovedEmployees);
    }
  };

  const handleSearch = selectedEmployees => {
    const vSelector = selector.filter(selected => !selectedEmployees.includes(selected.value));
    setModalShow(false);
    if (vSelector.length === selector.length) return;
    const employeesToAdd = selector
      .filter(selected => selectedEmployees.includes(selected.value))
      .map(employee => ({ ...basicOvertime(), employeeId: employee.value, employee }));
    setFieldValue('overtimes', [...values.overtimes, ...employeesToAdd]);
    setSelector(vSelector);
  };

  const { onHide, submitVariant, errors, touched } = props;
  const { overtimes } = values;
  return (
    <Form>
      <div className="info-box ml-0">
        <h4 className="text-uppercase">Selecciona Trabajadores</h4>
        <p className="info">
          <span className="full-width">Seleccione trabajadores para incluir en el proceso</span>
        </p>
      </div>
      <Row className="align-items-center mb-3 mb-md-0">
        <Col md={5}>
          <Field name="employeeSelector">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                label="Generar Para"
                placeholder="Seleccionar Empleado"
                options={selector}
                defaultValue="all_employees"
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                setFieldTouched={() => setFieldTouched(field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={6} md={2} xl={2}>
          <Button variant="primary" onClick={handleSelector} style={{ marginTop: '5px' }}>
            Agregar
          </Button>
        </Col>
        <Col>
          <ButtonTooltip
            variant="circle-primary"
            className="advance-search"
            text="Búsqueda Avanzada"
            onClick={() => setModalShow(true)}
          >
            <Icon className="w-100 h-100" icon="people-circle" />
          </ButtonTooltip>
        </Col>
      </Row>
      <Row>
        <Table responsive>
          <thead>
            <tr>
              <th>NOMBRE</th>
              <th className="text-center">FECHA</th>
              <th className="text-center" style={{ width: '120px' }}>
                HORAS
              </th>
              <th className="text-center" style={{ width: '120px' }}>
                MINUTOS
              </th>
              <th className="text-center" style={{ width: '120px' }}>
                VALOR
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {overtimes.map((overtime, index) => (
              <tr key={`overtime-${index.toString()}`}>
                <td className="employee-name">
                  <DatatableEmployeeName
                    item={overtime.employee}
                    fileName="fileInfo"
                    name="fullName"
                    fileUrl="fileUrl"
                  />
                </td>
                <td>
                  <Field name={`overtimes[${index}][date]`}>
                    {({ field }) => (
                      <FormikDatePicker
                        {...field}
                        isOutsideRange={() => false}
                        placeholder="dd/mm/aaaa"
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                        margin="mb-0"
                      />
                    )}
                  </Field>
                </td>
                <td>
                  <Field name={`overtimes[${index}][parsedHours]`}>
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        rightAddon="hrs"
                        placeholder="1"
                        fieldName={`overtimes[${index}][hours]`}
                        value={overtime.hours}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        touched={touched}
                        margin="mb-0"
                      />
                    )}
                  </Field>
                </td>
                <td>
                  <Field name={`overtimes[${index}][parsedMinutes]`}>
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        rightAddon="min"
                        placeholder="0"
                        fieldName={`overtimes[${index}][minutes]`}
                        value={overtime.minutes}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        touched={touched}
                        margin="mb-0"
                      />
                    )}
                  </Field>
                </td>
                <td>
                  <Field name={`overtimes[${index}][hourValue]`}>
                    {({ field }) => (
                      <FormikSelect
                        {...field}
                        abbr
                        placeholder="50%"
                        options={hourValues}
                        onChange={data => setFieldValue(field.name, data ? data.value : '')}
                        setFieldTouched={() => setFieldTouched(field.name)}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </td>
                <td>
                  <ButtonTooltip
                    onClick={() => removeFromTable(overtime.employee, 'destroy')}
                    variant="circle-danger"
                    className="btn-circle"
                    size="sm"
                    text="Eliminar"
                  >
                    <Icon icon="trash" />
                  </ButtonTooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Row>
      <Row className="d-flex justify-content-end m-t-10 m-b-30">
        <Col md={2}>
          <Button type="submit" disabled={isSubmitting} variant={submitVariant} block onClick={onHide}>
            Finalizar
          </Button>
        </Col>
      </Row>
      <SimpleCenteredModal
        title="Buscar Empleados"
        body={
          <EmployeeSearchModal
            customParams={{ active: true }}
            handleClose={() => setModalShow(false)}
            formRequest={handleSearch}
          />
        }
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
    </Form>
  );
};

const setInitialValues = () => {
  return {
    overtimes: [],
    employeeSelector: 'all_employees'
  };
};

const validationSchema = Yup.object().shape({
  overtimes: Yup.array().of(
    Yup.object().shape(
      {
        date: Yup.string().required('Debes seleccionar una fecha'),
        hours: Yup.number().when('minutes', {
          is: val => val > 0,
          then: Yup.number().nullable(),
          otherwise: Yup.number()
            .required('Debes ingresar la cantidad de horas')
            .positive('Debe ser mayor a 0')
            .max(23, 'Debe ser menor a 24 horas')
        }),
        minutes: Yup.number().when('hours', {
          is: val => val > 0,
          then: Yup.number().nullable(),
          otherwise: Yup.number()
            .required('Debes ingresar la cantidad de minutos')
            .positive('Debe ser mayor a 0')
            .max(59, 'Debe ser menor a 60 minutos')
        })
      },
      [['hours', 'minutes']]
    )
  )
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: props => setInitialValues(props),
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(OvertimesForm);
